import React from "react";
import PropTypes from "prop-types";
import { FiShoppingCart } from "react-icons/fi";
import Button, { ButtonPurpose } from "shared/Button";
import { Modal } from "shared/Modal";
import styles from "./PlaceOrderModal.module.css";

/**
 * Final confirmation before an admin places an order (scope figure 6.9).
 *
 * Checkout used to place the order on the first click, so a mis-click was an
 * order — there was no step between a full cart and a committed order. This is
 * that step: it restates what is about to be bought, what it costs, where it
 * ships, where it bills to and what pays for it, and only then offers "Place
 * Your Order".
 *
 * Kevin Shay (2026-09-14): billing is always the IBO's Primary address and is
 * not selectable.
 *
 * Neither block is editable here. Shipping used to carry its own dropdown, but
 * that put the choice in two places — the cart's IBO Details card also shows
 * the destination — and a confirmation step that can still change the order is
 * not a confirmation. Per Dylan's "Select Address" design the choice is made by
 * the pencil on the IBO Details card; this screen only restates it.
 */
export function PlaceOrderModal({
  isOpen,
  onClose,
  onConfirm,
  isPlacing,
  items,
  subTotal,
  tax,
  total,
  billingAddress,
  shipToAddress,
  isTempShipTo,
  payment,
}) {
  const count = items.reduce((sum, item) => sum + (Number(item.qty) || 1), 0);
  const heading = `Your Cart Has ${count} Item${count === 1 ? "" : "s"}`;

  return (
    <Modal
      title={heading}
      isOpen={isOpen}
      onClose={onClose}
      className={styles.modal}
    >
      <div className={styles.cartBadge} aria-hidden="true">
        <FiShoppingCart size={20} />
      </div>

      <ul className={styles.items}>
        {items.map((item) => (
          <li className={styles.item} key={item.id}>
            {item.image ? (
              <img className={styles.thumb} src={item.image} alt="" />
            ) : (
              <span className={styles.thumbFallback} aria-hidden="true">
                {(item.name || "?").trim().charAt(0).toUpperCase()}
              </span>
            )}
            <span className={styles.itemName}>
              {item.name}
              {/* Qty is only worth the extra line when it isn't the usual 1. */}
              {(Number(item.qty) || 1) > 1 ? (
                <span className={styles.itemQty}>{`Qty ${item.qty}`}</span>
              ) : null}
            </span>
            <span className={styles.itemPrice}>{item.price}</span>
          </li>
        ))}
      </ul>

      <div className={styles.totals}>
        <div className={styles.totalRow}>
          <span>Subtotal</span>
          <span className={styles.amount}>{subTotal}</span>
        </div>
        <div className={`${styles.totalRow} ${styles.taxRow}`}>
          <span>Tax</span>
          <span>{tax}</span>
        </div>
        <div className={`${styles.totalRow} ${styles.grandTotal}`}>
          <span>Order Total</span>
          <span>{total}</span>
        </div>
      </div>

      <div className={styles.block}>
        <h4 className={styles.blockLabel}>
          <span>Ship To</span>
          {isTempShipTo ? (
            <span
              className={styles.blockTag}
              title="A one-off address for this order. It is not saved to the IBO's record in CRM."
            >
              Temporary
            </span>
          ) : null}
        </h4>
        {shipToAddress ? (
          <p className={styles.blockValue}>{shipToAddress}</p>
        ) : (
          <p
            className={styles.blockMissing}
            title="No active address came back from the IBO directory, so this order won't record a destination."
          >
            Not available
          </p>
        )}
      </div>

      <div className={styles.block}>
        <h4 className={styles.blockLabel}>Billing Address</h4>
        {billingAddress ? (
          <p className={styles.blockValue}>{billingAddress}</p>
        ) : (
          <p
            className={styles.blockMissing}
            title="No primary address came back from the IBO directory."
          >
            Not available
          </p>
        )}
      </div>

      <div className={styles.block}>
        <h4 className={styles.blockLabel}>Pay With</h4>
        {payment ? (
          <p className={styles.blockValue}>
            <span className={styles.cardBrand}>{payment.brand}</span>
            {`*${payment.last4}`}
          </p>
        ) : (
          // Payment integration is still pending, so there is nothing to charge.
          // Saying so here is better than an empty row that reads as a bug, and
          // better than blocking a flow admins are expected to use meanwhile.
          <p
            className={styles.blockMissing}
            title="Payment integration is pending — no card is charged when an admin places an order."
          >
            No card charged — payment integration pending
          </p>
        )}
      </div>

      <div className={styles.actions}>
        <Button
          type="button"
          purpose={ButtonPurpose.generic}
          title="Return to the cart without placing this order."
          onClick={onClose}
          disabled={isPlacing}
        >
          Cancel
        </Button>
        <Button
          type="button"
          purpose={ButtonPurpose.save}
          title="Place this order for the IBO."
          onClick={onConfirm}
          disabled={isPlacing || !items.length}
        >
          {isPlacing ? "Placing Order…" : "Place Your Order"}
        </Button>
      </div>
    </Modal>
  );
}

PlaceOrderModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  isPlacing: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.string,
      qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  subTotal: PropTypes.string,
  tax: PropTypes.string,
  total: PropTypes.string,
  billingAddress: PropTypes.string,
  shipToAddress: PropTypes.string,
  isTempShipTo: PropTypes.bool,
  payment: PropTypes.shape({
    brand: PropTypes.string,
    last4: PropTypes.string,
  }),
};

PlaceOrderModal.defaultProps = {
  isOpen: false,
  isPlacing: false,
  items: [],
  tax: "—",
  billingAddress: null,
  shipToAddress: null,
  isTempShipTo: false,
  payment: null,
};

export default PlaceOrderModal;
